/**
 * <Textarea> — multiline text input primitive.
 *
 * Uses `dir="auto"` always — Textareas commonly hold mixed Arabic/English
 * (notes, descriptions, comments), so per-paragraph direction resolution is
 * the right default. Unlike `<Input>`, there's no forced-LTR subset here
 * (free-text content has no Latin-always guarantee).
 */
import * as React from "react";
import { cn, cva, type VariantProps } from "../../utils";

export const textareaVariants = cva(
  cn(
    "block w-full rounded-md border appearance-none",
    "text-body bg-surface-elevated text-default border-strong",
    "placeholder:text-muted",
    "focus-visible:focus-ring focus-visible:border-primary",
    "disabled:opacity-50 disabled:pointer-events-none",
    "read-only:bg-surface-sunken read-only:cursor-default",
    "data-[invalid=true]:border-danger data-[invalid=true]:focus-visible:border-danger",
    "transition-colors duration-fast ease-out",
    // Sensible default height; `rows` prop still wins.
    "min-h-24 resize-y",
  ),
  {
    variants: {
      density: {
        comfortable: "px-md py-sm",
        compact: "px-sm py-xs",
      },
    },
    defaultVariants: { density: "comfortable" },
  },
);

export type TextareaProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "dir"> &
  VariantProps<typeof textareaVariants> & {
    /** Mark as invalid — drives danger border + `aria-invalid`. */
    invalid?: boolean;
  };

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, density, invalid, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      dir="auto"
      className={cn(textareaVariants({ density }), className)}
      aria-invalid={invalid ? true : undefined}
      data-invalid={invalid ? "true" : undefined}
      {...rest}
    />
  );
});
